import { CourseStatus } from "@/lib/constants";
import { useAuth } from "@/lib/AuthContext";
import { logger } from "@/lib/logger";
import { supabase } from "@/lib/supabase-unified";
import { useQuery } from "@tanstack/react-query";

async function fetchDraftViewerAccess(): Promise<boolean> {
  const { data, error } = await supabase.rpc("current_user_can_view_drafts");
  if (error) {
    logger.warn("[draft-viewer] RPC failed; defaulting to non-viewer", error);
    return false;
  }
  return data === true;
}

/** Same RPC as useDraftViewer, with ready flag for redirect/gating UX. */
export function useDraftViewerGate(): { canViewDrafts: boolean; isReady: boolean } {
  const { user } = useAuth();
  const { data, isPending } = useQuery({
    queryKey: ["draft-viewer", user?.id ?? null],
    queryFn: fetchDraftViewerAccess,
    staleTime: 30 * 60 * 1000,
  });
  return { canViewDrafts: data === true, isReady: !isPending };
}

/**
 * Whether the *current* user may see non-published (status < 3) courses/lessons.
 */
export function useDraftViewer(): boolean {
  return useDraftViewerGate().canViewDrafts;
}

export function useEffectiveMinStatus(): number {
  return useDraftViewer() ? CourseStatus.Retired : CourseStatus.Published;
}

export {
  courseHiddenFromDraftViewers,
  effectiveMinStatusForCourse,
} from "@/lib/draftVisibility";
